import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Target, Send, Snowflake, RefreshCw, Clock, Mail, Sparkles, Check, Trash2, ChevronDown, User as UserIcon } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  retargetingStats, retargetingColdLeads, retargetingRun,
  retargetDraftsGenerate, retargetDraftsList, retargetDraftUpdate, retargetDraftDelete, retargetDraftsSend,
} from '../lib/marketingApi';

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' }); } catch (e) { return iso; }
}

const STATUS_STYLE = {
  draft: 'bg-slate-100 text-slate-600', approved: 'bg-emerald-100 text-emerald-700',
  sent: 'bg-[#0035A8]/10 text-[#0035A8]', failed: 'bg-rose-100 text-rose-600',
};

export default function Retargeting() {
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const [tab, setTab] = useState('leads');
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [drafts, setDrafts] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [edit, setEdit] = useState({});

  const loadStats = useCallback(() => { retargetingStats().then(setStats).catch(() => {}); }, []);
  const loadLeads = useCallback(async () => {
    setItems(null);
    try { const d = await retargetingColdLeads({ limit: 200 }); setItems(d.items || []); }
    catch (e) { toast.error('Failed to load cold leads'); setItems([]); }
  }, []);
  const loadDrafts = useCallback(async () => {
    try { const d = await retargetDraftsList(); setDrafts(d.items || []); }
    catch (e) { setDrafts([]); }
  }, []);

  useEffect(() => { loadStats(); loadLeads(); loadDrafts(); }, [loadStats, loadLeads, loadDrafts]);

  const toggle = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
  const toggleAll = () => {
    if (!items) return;
    setSelected(selected.size === items.length ? new Set() : new Set(items.map((i) => i.id)));
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const ids = Array.from(selected);
      const r = await retargetDraftsGenerate(ids.length ? { contact_ids: ids } : { limit: 25 });
      toast.success(`Generated ${r.created} draft${r.created === 1 ? '' : 's'}`);
      setSelected(new Set()); await loadDrafts(); setTab('drafts');
    } catch (e) { toast.error(e.response?.data?.detail || 'Draft generation failed'); }
    finally { setGenerating(false); }
  };

  const saveDraft = async (dft) => {
    try { await retargetDraftUpdate(dft.id, { subject: edit.subject, body: edit.body }); toast.success('Draft saved'); setExpanded(null); loadDrafts(); }
    catch (e) { toast.error('Save failed'); }
  };
  const setStatus = async (dft, status) => {
    try { await retargetDraftUpdate(dft.id, { status }); loadDrafts(); }
    catch (e) { toast.error('Failed'); }
  };
  const removeDraft = async (dft) => {
    try { await retargetDraftDelete(dft.id); loadDrafts(); }
    catch (e) { toast.error('Failed'); }
  };
  const sendApproved = async () => {
    const ids = (drafts || []).filter((d) => d.status === 'approved').map((d) => d.id);
    if (!ids.length) { toast.error('Approve at least one draft first'); return; }
    setSending(true);
    try { const r = await retargetDraftsSend(ids); toast.success(`Sent ${r.sent}, ${r.failed} failed`); loadDrafts(); loadStats(); loadLeads(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  const w = stats?.window;
  const approvedCount = (drafts || []).filter((d) => d.status === 'approved').length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="retargeting-page">
      <AdminHeader active="retargeting" actions={canEdit && (
        tab === 'leads' ? (
          <button onClick={generate} disabled={generating} data-testid="retargeting-generate-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#002B87] disabled:opacity-60">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate AI drafts{selected.size ? ` (${selected.size})` : ''}
          </button>
        ) : (
          <button onClick={sendApproved} disabled={sending || approvedCount === 0} data-testid="retargeting-send-approved-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#002B87] disabled:opacity-60">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send approved ({approvedCount})
          </button>
        )
      )} />
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><Target className="h-4.5 w-4.5" /></span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Cold-lead retargeting</h1>
            <p className="text-sm text-slate-500">Leads quiet for {w ? `${w.min_days}–${w.max_days}` : '1–2'} weeks — draft persona-aware emails, review, then send in bulk</p>
          </div>
        </div>

        {stats && (
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="retargeting-stat-cold">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400"><Snowflake className="h-3.5 w-3.5" /> Cold now</div>
              <div className="mt-1 text-2xl font-bold text-sky-600">{(stats.cold || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="retargeting-stat-30d">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400"><RefreshCw className="h-3.5 w-3.5" /> Re-engaged (30d)</div>
              <div className="mt-1 text-2xl font-bold text-emerald-600">{(stats.reengaged_30d || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="retargeting-stat-total">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400"><Mail className="h-3.5 w-3.5" /> Total re-engaged</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{(stats.sent_total || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400"><Clock className="h-3.5 w-3.5" /> Auto sweep</div>
              <div className="mt-1 text-sm font-semibold text-slate-700">{w?.auto ? `Daily · ${String(w.hour_utc).padStart(2, '0')}:30 UTC` : 'Off'}</div>
              <div className="mt-0.5 text-xs text-slate-400">cooldown {w?.cooldown_days}d · max {w?.max_sends} sends</div>
            </div>
          </div>
        )}

        <div className="mb-4 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-100 p-1">
          <button onClick={() => setTab('leads')} data-testid="retargeting-tab-leads" className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-all ${tab === 'leads' ? 'bg-white text-[#0035A8] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Cold leads{items ? ` (${items.length})` : ''}</button>
          <button onClick={() => setTab('drafts')} data-testid="retargeting-tab-drafts" className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-all ${tab === 'drafts' ? 'bg-white text-[#0035A8] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Email drafts{drafts ? ` (${drafts.length})` : ''}</button>
        </div>

        {tab === 'leads' ? (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
              <div className="col-span-1"><input type="checkbox" checked={items && items.length > 0 && selected.size === items.length} onChange={toggleAll} data-testid="retargeting-select-all" /></div>
              <div className="col-span-3">Name / Email</div><div className="col-span-2">Company</div>
              <div className="col-span-2">Assigned to</div>
              <div className="col-span-2 text-center">Days cold</div><div className="col-span-2 text-center">Re-engaged</div>
            </div>
            {items === null ? (
              <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-16 text-slate-400" data-testid="retargeting-empty"><Snowflake className="h-8 w-8" /><p className="text-sm">No cold leads right now — everyone's warm. 🎉</p></div>
            ) : items.map((c) => (
              <div key={c.id} data-testid={`retargeting-row-${c.email}`}
                className="grid w-full grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3 text-left hover:bg-slate-50 md:grid-cols-12 md:items-center md:gap-3">
                <div className="col-span-1"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} data-testid={`retargeting-select-${c.email}`} /></div>
                <button onClick={() => navigate(`/admin/contacts/${c.id}`)} className="col-span-3 min-w-0 text-left">
                  <div className="truncate text-sm font-semibold text-slate-900">{c.name || c.email}</div>
                  <div className="truncate text-xs text-slate-400">{c.email}</div>
                </button>
                <div className="col-span-2 truncate text-sm text-slate-600">{c.company || '—'}</div>
                <div className="col-span-2 min-w-0 truncate text-sm">
                  {c.owner ? <span className="inline-flex items-center gap-1 text-slate-700"><UserIcon className="h-3 w-3 text-slate-400" />{c.owner}</span> : <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Unassigned</span>}
                </div>
                <div className="col-span-2 text-center"><span className="inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700">{c.days_cold ?? '—'}d</span></div>
                <div className="col-span-2 text-center text-sm text-slate-600">{c.retargeting?.count ? <span>{c.retargeting.count}× · {fmt(c.retargeting.last_sent_at)}</span> : <span className="text-slate-400">Never</span>}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="retargeting-drafts">
            {drafts === null ? (
              <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : drafts.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 bg-white py-16 text-slate-400" data-testid="retargeting-drafts-empty"><Sparkles className="h-8 w-8" /><p className="text-sm">No drafts yet — select cold leads and click "Generate AI drafts".</p></div>
            ) : drafts.map((dft) => (
              <div key={dft.id} className="rounded-lg border border-slate-200 bg-white" data-testid={`draft-${dft.email}`}>
                <div className="flex items-center gap-3 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2"><span className="truncate text-sm font-semibold text-slate-900">{dft.name || dft.email}</span><span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLE[dft.status]}`}>{dft.status}</span></div>
                    <div className="truncate text-xs text-slate-400">{dft.email} · {dft.subject}</div>
                  </div>
                  {canEdit && dft.status !== 'sent' && (
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setStatus(dft, dft.status === 'approved' ? 'draft' : 'approved')} data-testid={`draft-approve-${dft.email}`}
                        className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold ${dft.status === 'approved' ? 'bg-emerald-600 text-white' : 'border border-emerald-500 text-emerald-600 hover:bg-emerald-50'}`}>
                        <Check className="h-3.5 w-3.5" /> {dft.status === 'approved' ? 'Approved' : 'Approve'}
                      </button>
                      <button onClick={() => { setExpanded(expanded === dft.id ? null : dft.id); setEdit({ subject: dft.subject, body: dft.body }); }} data-testid={`draft-edit-${dft.email}`}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded === dft.id ? 'rotate-180' : ''}`} /> Edit</button>
                      <button onClick={() => removeDraft(dft)} data-testid={`draft-delete-${dft.email}`} className="inline-flex items-center rounded-md border border-slate-300 px-2 py-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </div>
                {expanded === dft.id && (
                  <div className="border-t border-slate-100 px-5 py-4">
                    <label className="text-xs font-medium text-slate-500">Subject
                      <input value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} data-testid={`draft-subject-${dft.email}`} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#0035A8]" /></label>
                    <label className="mt-3 block text-xs font-medium text-slate-500">Body
                      <textarea value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} rows={8} data-testid={`draft-body-${dft.email}`} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#0035A8]" /></label>
                    <div className="mt-2 text-[11px] text-slate-400">Persona basis: {dft.persona_basis || 'general'}</div>
                    <button onClick={() => saveDraft(dft)} data-testid={`draft-save-${dft.email}`} className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#002B87]">Save draft</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
